import { ColDef } from 'ag-grid-community'
import {
  Alert,
  COMMON_DEFAULT_COLUMN_OPTIONS,
  Loading,
  Modal,
  OperationButton,
  Table
} from 'components/core-v2'
import { required } from 'components/core-v2/molecules/fields/validators'
import { format } from 'date-fns'
import { ReactNode } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { ApiAssetPriceWithHistory, ApiAssetPriceWithOverride, ApiOverrideInfo } from '@/api'
import { Field, FormError, Statistic } from '@/components/core-v2/molecules'
import { Currency, formatCurrencyFull } from '@/utilities/currency'
import { formatToDateDisplay } from '@/utilities/date'

import classes from './price-override.module.scss'

interface PriceHistoryTableProps {
  allPrices: ApiAssetPriceWithOverride[]
  assetPriceWithHistory: ApiAssetPriceWithHistory
}

const getSourceLabel = (price?: ApiAssetPriceWithOverride) =>
  price?.displayName || price?.source || ''

const getSourceTooltip = (price?: ApiAssetPriceWithOverride) => {
  const displayName = price?.displayName
  const source = price?.source

  if (displayName && source && displayName !== source) {
    return `${displayName} (${source})`
  }

  return displayName || source || ''
}

const COLUMN_DEFS: ColDef[] = [
  {
    headerName: 'Price',
    field: 'price',
    minWidth: 200,
    flex: 0.2,
    valueFormatter: params => formatCurrencyFull(params.value, params.context.currency as Currency)
  },
  {
    headerName: 'Source',
    field: 'source',
    minWidth: 200,
    valueGetter: params => getSourceLabel(params.data),
    cellRenderer: params => <span title={getSourceTooltip(params.data)}>{params.value}</span>
  },
  {
    headerName: 'Value date',
    field: 'valueDate',
    minWidth: 220,
    type: 'rightAligned',
    valueFormatter: params => formatToDateDisplay(params.value)
  },
  {
    headerName: 'Received',
    field: 'receivedDateTime',
    minWidth: 220,
    type: 'rightAligned',
    valueFormatter: params => format(new Date(params.value), 'dd/MM/yyyy HH:mm')
  }
]

const PriceHistoryTable = ({ allPrices, assetPriceWithHistory }: PriceHistoryTableProps) => (
  <Table
    agGridProps={{
      columnDefs: COLUMN_DEFS,
      defaultColDef: COMMON_DEFAULT_COLUMN_OPTIONS,
      headerHeight: 60,
      rowData: allPrices,
      rowHeight: 44,
      context: {
        currency: assetPriceWithHistory.currency
      }
    }}
    height="300px"
  />
)

export type PriceOverrideProps = {
  assetPriceWithHistory: ApiAssetPriceWithHistory
  errors?: { [key: string]: string }
  isLoadingOverrideAvailability?: boolean
  isSubmitting?: boolean
  onClose: () => void
  onPriceOverride: (updatedPrice: number) => void
  overrideInfo?: ApiOverrideInfo
  readonly: boolean
}

const formatSaTime = (value?: string | null) => {
  if (!value) return null

  return new Intl.DateTimeFormat('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Africa/Johannesburg'
  }).format(new Date(value))
}

export function PriceOverride({
  assetPriceWithHistory,
  errors = {},
  isLoadingOverrideAvailability = false,
  isSubmitting = false,
  onClose,
  onPriceOverride,
  overrideInfo,
  readonly
}: PriceOverrideProps) {
  const form = useForm()

  const onSubmit = ({ newPrice }: { newPrice: string }) => {
    if (Number(newPrice) !== assetPriceWithHistory?.currentPrice?.price) {
      onPriceOverride(Number(newPrice))
    }
  }

  const currentPriceValue = formatCurrencyFull(
    assetPriceWithHistory.currentPrice.price,
    assetPriceWithHistory.currency as Currency
  )

  const valueDate = formatToDateDisplay(assetPriceWithHistory.currentPrice.valueDate)
  const noOverrideStartTime = formatSaTime(overrideInfo?.noOverrideStartTime)
  const noOverrideEndTime = formatSaTime(overrideInfo?.noOverrideEndTime)

  const showTable = assetPriceWithHistory?.allPrices?.length > 0
  const showErrors = Object.keys(errors).length > 0
  let priceUpdateContent: ReactNode

  if (isLoadingOverrideAvailability) {
    priceUpdateContent = <Loading className="mt-4" />
  } else if (readonly) {
    priceUpdateContent = (
      <Alert className={classes.alert} title="Unable to update price" type="warning">
        {noOverrideStartTime && noOverrideEndTime
          ? `Price updates are only allowed between ${noOverrideEndTime} and ${noOverrideStartTime} SA time.`
          : 'Price updates are only allowed during the configured SA time window.'}
      </Alert>
    )
  } else {
    priceUpdateContent = (
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={classes.priceUpdateForm}>
            <div className={classes.newPriceFieldWrapper}>
              <Field
                id="newPrice"
                label="New price"
                step="any"
                type="number"
                validators={required}
              />
            </div>
            <OperationButton
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              operationType="progress"
              type="submit"
            >
              Update price
            </OperationButton>
            <div className={classes.formError}>
              <FormError isShowing={showErrors} title="Something went wrong">
                {Object.values(errors).map(error => (
                  <p key={error}>{error}</p>
                ))}
              </FormError>
            </div>
          </div>
        </form>
      </FormProvider>
    )
  }

  return (
    <Modal.Wrapper isOpen aria-labelledby="price-update-modal" width="lg" onClose={onClose}>
      <Modal.Header id="price-update-modal" onClose={onClose}>
        {assetPriceWithHistory.assetName}
      </Modal.Header>
      <Modal.Body>
        <div className={classes.top}>
          <div className={classes.currentPriceWrapper}>
            <Statistic.Wrapper>
              <Statistic.Header>Current price</Statistic.Header>
              <Statistic.Stat>{currentPriceValue}</Statistic.Stat>
            </Statistic.Wrapper>
            <span className="mt-2">Value date: {valueDate}</span>
          </div>
          <div className={classes.priceUpdateWrapper}>{priceUpdateContent}</div>
        </div>

        {showTable && (
          <PriceHistoryTable
            allPrices={assetPriceWithHistory.allPrices}
            assetPriceWithHistory={assetPriceWithHistory}
          />
        )}
      </Modal.Body>
    </Modal.Wrapper>
  )
}
